import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PROJECTS, FILTERS } from "@/data/content";

const Lightbox = ({ project, onClose }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-10"
        data-testid="project-lightbox"
    >
        <div className="absolute inset-0 bg-night/90 backdrop-blur-sm" onClick={onClose} />
        <motion.figure
            initial={{ scale: 0.94, y: 24 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-full max-w-4xl bg-ivory"
        >
            <img src={project.src} alt={project.title} className="max-h-[68vh] w-full object-cover" />
            <figcaption className="flex flex-wrap items-end justify-between gap-4 p-6 sm:p-8">
                <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra">
                        {project.category} — {project.location}
                    </p>
                    <h3 className="mt-2 font-serif text-2xl font-medium text-ink">{project.title}</h3>
                    <p className="mt-2 max-w-lg text-sm font-light leading-relaxed text-mute">{project.text}</p>
                </div>
                <button
                    data-testid="lightbox-close-btn"
                    onClick={onClose}
                    aria-label="Close"
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-wood/20 text-wood transition-colors duration-300 hover:bg-ink hover:text-ivory"
                >
                    <X size={16} />
                </button>
            </figcaption>
        </motion.figure>
    </motion.div>
);

const FilterTabs = ({ active, onChange }) => (
    <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
            <button
                key={f.id}
                data-testid={f.testid}
                onClick={() => onChange(f.id)}
                className={`rounded-full border px-5 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                    active === f.id ? "border-ink bg-ink text-ivory" : "border-wood/20 text-mute hover:border-wood/50 hover:text-ink"
                }`}
            >
                {f.label}
            </button>
        ))}
    </div>
);

const ProjectCard = ({ project, index, onOpen }) => (
    <motion.article
        layout
        data-testid={`project-card-${project.id}`}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.5, delay: 0.05 * index, ease: "easeInOut" }}
        className={`group cursor-pointer ${index % 3 === 1 ? "lg:mt-12" : ""}`}
        onClick={onOpen}
    >
        <div className="relative">
            <div className="absolute -inset-0 translate-x-2.5 translate-y-2.5 border border-wood/15 transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1" />
            <div className="relative overflow-hidden">
                <img
                    src={project.src}
                    alt={project.title}
                    loading="lazy"
                    className={`${project.aspect} w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]`}
                />
                <div className="absolute inset-0 bg-night/0 transition-colors duration-500 group-hover:bg-night/25" />
                <span className="absolute bottom-4 right-4 flex h-10 w-10 translate-y-3 items-center justify-center rounded-full bg-ivory text-ink opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <Plus size={16} />
                </span>
            </div>
        </div>
        <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-terra">
            {project.category} — {project.location}
        </p>
        <h3 className="mt-2 font-serif text-xl font-medium text-ink transition-colors duration-300 group-hover:text-terra">{project.title}</h3>
    </motion.article>
);

export const Gallery = () => {
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(null);
    const visible = PROJECTS.filter((p) => filter === "all" || p.filter === filter);

    return (
        <section id="works" className="relative bg-sand py-20 lg:py-32">
            <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
                <Reveal>
                    <div className="flex flex-wrap items-end justify-between gap-8">
                        <div className="flex items-center gap-4">
                            <span className="h-px w-10 bg-terra/50" />
                            <span className="text-xs uppercase tracking-[0.25em] text-timber">
                                {visible.length} {visible.length === 1 ? "Project" : "Projects"}
                            </span>
                        </div>
                        <FilterTabs active={filter} onChange={setFilter} />
                    </div>
                </Reveal>
                <motion.div layout className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                    <AnimatePresence mode="popLayout">
                        {visible.map((p, i) => (
                            <ProjectCard key={p.id} project={p} index={i} onOpen={() => setActive(p)} />
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>
            <AnimatePresence>{active && <Lightbox project={active} onClose={() => setActive(null)} />}</AnimatePresence>
        </section>
    );
};
